import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useApp } from '../context/AppContext';
import { storiesApi } from '../api/stories';
import { styleCardsApi } from '../api/styleCards';
import { useSyncStoryContext } from '../hooks/useSyncStoryContext';
import { registerUnsavedLeaveGuard } from '../utils/draftLeaveGuard';
import DialScale from '../components/StyleRoom/DialScale';
import { DIAL_EXPLAINERS } from '../utils/dialMeanings';
import ConfirmModal from '../components/Common/ConfirmModal';
import Loading from '../components/Common/Loading';
import StoryPicker from '../components/Common/StoryPicker';
import { draftThisScenePath } from '../utils/sceneWritingPath';
import { styleCardsPath } from '../utils/styleCardsPath';
import {
  clampWords,
  countWords,
  deleteStyleWarning,
  foldStyleNotes,
  styleHasInstruction,
  usedInLabel,
} from '../utils/styleRoom';
import { STYLE_PRESETS } from '../utils/stylePresets';
import {
  BOOK_STYLE,
  COPIED_FROM,
  DELETE_STYLE,
  DISCARD_UNSAVED,
  HOW_WRITING_SHOULD_SOUND,
  KEEP_EDITING,
  LOADING_STYLE_NOTES,
  MAKE_A_COPY,
  MOVED_NOTES_INTO_STYLE,
  NEW_STYLE,
  OPEN_SCENE,
  OWN_WRITING_LEDE,
  PASTE_A_SAMPLE_FIRST,
  PICK_A_STARTING_SOUND,
  PUT_ALL_BACK_ON_BOOK_STYLE,
  SAMPLE_OF_THE_SOUND,
  SAMPLE_WORD_LIMIT,
  SCENES_BACK_ON_BOOK_STYLE,
  SELECT_BOOK_FOR_STYLE_NOTE,
  START_FROM_MY_OWN_WRITING,
  START_FROM_SCRATCH,
  STYLE_ACTIONS_FOR,
  STYLE_FIELD_OPTIONAL,
  STYLE_NEEDS_SOMETHING,
  STYLE_RENAME,
  STYLE_ROOM_SAVE,
  SAVED,
  THIS_IS_NOW_YOUR_BOOK_STYLE,
  UNDO_ACTION,
  UNSAVED_STYLE_CONFIRM,
  USE_FOR_THIS_BOOK,
  USE_THE_BOOK_STYLE,
  YOUR_STYLES,
} from '../utils/writerCopy';
import './StyleRoom.css';

const unwrapList = (res) => (Array.isArray(res) ? res : res?.data || []);

const byRecency = (a, b) => {
  const at = new Date(a.last_activity_at || a.created_at).getTime();
  const bt = new Date(b.last_activity_at || b.created_at).getTime();
  return bt - at;
};

const emptyDraft = () => ({
  name: '',
  text: '',
  sample: '',
  dials: {},
  moved: false,
  copiedFrom: '',
});

const DIALS = DIAL_EXPLAINERS;

const dialSignature = (dials) => DIALS.map((dial) => {
  const stop = Number(dials?.[dial.key]);
  return stop >= 1 && stop <= 5 ? String(stop) : '';
}).join(',');

const draftsDiffer = (a, b) => (
  (a?.name || '') !== (b?.name || '')
  || (a?.text || '') !== (b?.text || '')
  || (a?.sample || '') !== (b?.sample || '')
  || dialSignature(a?.dials) !== dialSignature(b?.dials)
);

const draftFromCard = (card, list) => {
  const fold = foldStyleNotes(card);
  const parent = (list || []).find((item) => item.id === card.started_from_card_id);
  return {
    name: card.name || NEW_STYLE,
    text: fold.text,
    sample: clampWords(card.exemplars || '', 150),
    dials: card.dials || {},
    moved: Boolean(fold.moved),
    copiedFrom: parent?.name || '',
  };
};

/**
 * Saved styles. One open style. Save writes that id and no other.
 * Tone, mechanics, lexicon, and direction stay in the database and
 * move into the plain-words box the first time a style is opened.
 */
export default function StyleRoom() {
  const { storyId: routeStoryId } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const { currentProject, currentStory, setProjectAndStory, storyListVersion } = useApp();
  const navigate = useNavigate();
  const openedId = routeStoryId || currentStory?.id || null;
  const { storyId, loadedStory } = useSyncStoryContext({ routeStoryId: openedId });

  const [pickerOpen, setPickerOpen] = useState(false);
  const [projectStories, setProjectStories] = useState(null);
  const [cards, setCards] = useState([]);
  const [stylesStatus, setStylesStatus] = useState('loading');
  const [openId, setOpenId] = useState(null);
  const [formOpen, setFormOpen] = useState(false);
  const [ownOpen, setOwnOpen] = useState(false);
  const [ownName, setOwnName] = useState('');
  const [ownSample, setOwnSample] = useState('');
  const [draft, setDraft] = useState(emptyDraft);
  const [usage, setUsage] = useState(null);
  const [undoIds, setUndoIds] = useState([]);
  const [menuId, setMenuId] = useState(null);
  const [selectName, setSelectName] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [note, setNote] = useState('');
  const [deleteAsk, setDeleteAsk] = useState(null);
  const [picking, setPicking] = useState(false);
  const [leaveOpen, setLeaveOpen] = useState(false);
  const openIdRef = useRef(null);
  const draftDirtyRef = useRef(false);
  const ownDirtyRef = useRef(false);
  const cardsRef = useRef([]);
  const nameRef = useRef(null);
  const askedNewRef = useRef(false);
  const baselineRef = useRef(emptyDraft());
  const pendingLeaveRef = useRef(null);
  const pickingRef = useRef(false);
  const unloadHandlerRef = useRef(null);
  const activeProjectId = currentProject?.id || loadedStory?.project_id || null;

  // One predicate for the in-page dialog and for reload / tab close.
  const styleIsDirty = useCallback(
    () => draftDirtyRef.current || ownDirtyRef.current,
    [],
  );

  // Attach in the same turn as the edit. A state flag committed in an
  // effect lags the refs the dialog already trusts, so reload can miss it.
  const syncUnloadGuard = useCallback(() => {
    if (!unloadHandlerRef.current) {
      unloadHandlerRef.current = (event) => {
        if (!styleIsDirty()) return undefined;
        event.preventDefault();
        event.returnValue = '';
        return '';
      };
    }
    const handler = unloadHandlerRef.current;
    window.removeEventListener('beforeunload', handler);
    if (styleIsDirty()) window.addEventListener('beforeunload', handler);
  }, [styleIsDirty]);

  useEffect(() => () => {
    const handler = unloadHandlerRef.current;
    if (handler) window.removeEventListener('beforeunload', handler);
  }, []);

  useEffect(() => {
    if (!activeProjectId) { setProjectStories(null); return undefined; }
    let cancelled = false;
    storiesApi.list(activeProjectId)
      .then((res) => { if (!cancelled) setProjectStories(unwrapList(res)); })
      .catch(() => { if (!cancelled) setProjectStories([]); });
    return () => { cancelled = true; };
  }, [activeProjectId, storyListVersion]);

  useEffect(() => {
    if (routeStoryId) { setPickerOpen(false); return; }
    if (currentStory?.id) {
      setPickerOpen(false);
      navigate(styleCardsPath(currentStory.id), { replace: true });
      return;
    }
    if (!currentProject) { setPickerOpen(true); return; }
    if (!projectStories || projectStories.length === 0) return;
    const mostRecent = [...projectStories].sort(byRecency)[0];
    setProjectAndStory(currentProject, mostRecent);
    navigate(styleCardsPath(mostRecent.id), { replace: true });
  }, [routeStoryId, currentStory?.id, currentProject, projectStories, navigate, setProjectAndStory]);

  const rememberDraft = useCallback((next, id) => {
    const copy = {
      name: next?.name || '',
      text: next?.text || '',
      sample: next?.sample || '',
      dials: { ...(next?.dials || {}) },
      moved: Boolean(next?.moved),
      copiedFrom: next?.copiedFrom || '',
    };
    baselineRef.current = copy;
    draftDirtyRef.current = false;
    if (id !== undefined) {
      openIdRef.current = id;
      setOpenId(id);
    }
    setDraft(copy);
    syncUnloadGuard();
    return copy;
  }, [syncUnloadGuard]);

  const showPicker = useCallback(() => {
    pickingRef.current = true;
    setPicking(true);
    setFormOpen(false);
    setOwnOpen(false);
    setOwnName('');
    setOwnSample('');
    ownDirtyRef.current = false;
    setUsage(null);
    syncUnloadGuard();
  }, [syncUnloadGuard]);

  const runOrAsk = useCallback((action) => {
    if (!draftDirtyRef.current && !ownDirtyRef.current) {
      action();
      return;
    }
    pendingLeaveRef.current = action;
    setLeaveOpen(true);
  }, []);

  const confirmLeave = () => {
    const action = pendingLeaveRef.current;
    pendingLeaveRef.current = null;
    setLeaveOpen(false);
    const saved = baselineRef.current;
    setDraft({ ...saved, dials: { ...(saved.dials || {}) } });
    draftDirtyRef.current = false;
    ownDirtyRef.current = false;
    setOwnName('');
    setOwnSample('');
    syncUnloadGuard();
    action?.();
  };

  const cancelLeave = () => {
    pendingLeaveRef.current = null;
    setLeaveOpen(false);
  };

  const showStyle = useCallback((card, { select = false, cardsNow, keepUsage = false } = {}) => {
    pickingRef.current = false;
    setPicking(false);
    setOwnOpen(false);
    if (!keepUsage) setUsage(null);
    if (!card) {
      openIdRef.current = null;
      setOpenId(null);
      setFormOpen(false);
      rememberDraft(emptyDraft(), null);
      return;
    }
    const list = cardsNow || cardsRef.current;
    setFormOpen(true);
    rememberDraft(draftFromCard(card, list), card.id);
    if (select) setSelectName(true);
  }, [rememberDraft]);

  const openStyle = useCallback((card, opts = {}) => {
    if (!opts.force && card?.id && card.id === openIdRef.current && !pickingRef.current) {
      if (opts.select) setSelectName(true);
      return;
    }
    runOrAsk(() => showStyle(card, opts));
  }, [runOrAsk, showStyle]);

  const load = useCallback(async () => {
    if (!storyId) return;
    setStylesStatus('loading');
    setError('');
    try {
      const list = await styleCardsApi.list(storyId);
      const next = list?.cards || [];
      cardsRef.current = next;
      setCards(next);
      setStylesStatus('ready');
      const current = openIdRef.current;
      const still = next.find((card) => card.id === current);
      const book = next.find((card) => card.id === list?.book_default_card_id)
        || next.find((card) => card.is_book_default);
      const pick = still || book || next[0] || null;
      if (draftDirtyRef.current || ownDirtyRef.current || pickingRef.current) return;
      if (!pick) {
        setFormOpen(false);
        rememberDraft(emptyDraft(), null);
        return;
      }
      setFormOpen(true);
      rememberDraft(draftFromCard(pick, next), pick.id);
    } catch (err) {
      setStylesStatus('error');
      setError(err?.message || 'Could not load your styles.');
    }
  }, [storyId, rememberDraft]);

  useEffect(() => { load(); }, [load]);

  const askRouteLeave = useCallback((resume) => {
    pendingLeaveRef.current = typeof resume === 'function' ? resume : null;
    setLeaveOpen(true);
  }, []);

  // The in-page dialog is the leave guard. isBlocked reads the refs so
  // Discard can navigate in the same turn, before React re-renders.
  // beforeunload uses that same predicate and is removed when it is clean.
  useEffect(() => registerUnsavedLeaveGuard(
    styleIsDirty,
    UNSAVED_STYLE_CONFIRM,
    askRouteLeave,
  ), [askRouteLeave, styleIsDirty]);

  useEffect(() => {
    if (!selectName) return undefined;
    const node = nameRef.current;
    if (!node) return undefined;
    node.focus();
    node.select();
    setSelectName(false);
    return undefined;
  }, [selectName, openId]);

  const editDraft = (patch) => {
    const next = { ...draft, ...patch };
    if (Object.prototype.hasOwnProperty.call(patch, 'sample')) {
      next.sample = clampWords(patch.sample, 150);
    }
    const dirty = draftsDiffer(next, baselineRef.current);
    draftDirtyRef.current = dirty;
    syncUnloadGuard();
    setNote('');
    setDraft(next);
  };

  const createNew = useCallback(async () => {
    if (!storyId) return;
    pickingRef.current = false;
    setPicking(false);
    setFormOpen(true);
    setOwnOpen(false);
    setBusy(true);
    setError('');
    rememberDraft(emptyDraft(), openIdRef.current);
    setSelectName(true);
    try {
      const card = await styleCardsApi.createBlank(storyId);
      if (!card) return;
      setCards((current) => {
        const next = [card, ...current.filter((item) => item.id !== card.id)];
        cardsRef.current = next;
        return next;
      });
      openIdRef.current = card.id;
      setOpenId(card.id);
      if (draftDirtyRef.current) {
        setDraft((current) => ({
          ...current,
          name: current.name || card.name || NEW_STYLE,
        }));
      } else {
        rememberDraft({
          name: card.name || NEW_STYLE,
          text: '',
          sample: clampWords(card.exemplars || '', 150),
          dials: card.dials || {},
          moved: false,
          copiedFrom: '',
        }, card.id);
        setSelectName(true);
      }
    } catch (err) {
      setError(err?.message || 'Could not make a new style.');
    } finally {
      setBusy(false);
    }
  }, [storyId, rememberDraft]);

  useEffect(() => {
    if (!storyId || searchParams.get('new') !== '1' || askedNewRef.current) return;
    askedNewRef.current = true;
    const next = new URLSearchParams(searchParams);
    next.delete('new');
    setSearchParams(next, { replace: true });
    runOrAsk(showPicker);
  }, [storyId, searchParams, setSearchParams, runOrAsk, showPicker]);

  const onCopy = async (card) => {
    if (!storyId || !card?.id) return;
    setMenuId(null);
    setBusy(true);
    setError('');
    try {
      const copy = await styleCardsApi.copyStyle(storyId, card.id);
      if (!copy) return;
      const nextCards = [copy, ...cardsRef.current.filter((item) => item.id !== copy.id)];
      cardsRef.current = nextCards;
      setCards(nextCards);
      showStyle(
        { ...copy, name: copy.name || `${card.name || NEW_STYLE} (copy)` },
        { select: true, cardsNow: [...nextCards, card] },
      );
    } catch (err) {
      setError(err?.message || 'Could not copy that style.');
    } finally {
      setBusy(false);
    }
  };

  const onSave = async () => {
    const id = openIdRef.current;
    if (!storyId || !id) return;
    setBusy(true);
    setError('');
    try {
      const sample = clampWords(draft.sample, 150);
      const saved = await styleCardsApi.saveOpen(storyId, id, {
        name: draft.name,
        resolved_text: draft.text,
        exemplars: sample,
        dials: draft.dials || {},
      });
      if (!saved) return;
      setCards((current) => {
        const next = current.map((item) => (
          item.id === saved.id ? { ...item, ...saved, exemplars: clampWords(saved.exemplars ?? sample, 150) } : item
        ));
        cardsRef.current = next;
        return next;
      });
      rememberDraft({
        name: saved.name || draft.name,
        text: saved.resolved_text ?? draft.text,
        sample: clampWords(saved.exemplars ?? sample, 150),
        dials: saved.dials || draft.dials || {},
        moved: false,
        copiedFrom: draft.copiedFrom,
      }, saved.id);
      setNote(SAVED);
    } catch (err) {
      setError(err?.message || 'Could not save this style.');
    } finally {
      setBusy(false);
    }
  };

  const onMakeBook = async (cardId) => {
    if (!storyId || !cardId) return;
    setMenuId(null);
    setBusy(true);
    setError('');
    try {
      if (openIdRef.current === cardId) {
        const sample = clampWords(draft.sample, 150);
        const differs = draftDirtyRef.current || draftsDiffer(draft, baselineRef.current);
        if (differs) {
          const saved = await styleCardsApi.saveOpen(storyId, cardId, {
            name: draft.name,
            resolved_text: draft.text,
            exemplars: sample,
            dials: draft.dials || {},
          });
          if (!saved) return;
          setCards((current) => {
            const next = current.map((item) => (
              item.id === saved.id ? { ...item, ...saved, exemplars: clampWords(saved.exemplars ?? sample, 150) } : item
            ));
            cardsRef.current = next;
            return next;
          });
          rememberDraft({
            name: saved.name || draft.name,
            text: saved.resolved_text ?? draft.text,
            sample: clampWords(saved.exemplars ?? sample, 150),
            dials: saved.dials || draft.dials || {},
            moved: false,
            copiedFrom: draft.copiedFrom,
          }, saved.id);
        }
      }
      await styleCardsApi.setBookStyle(storyId, cardId);
      setCards((current) => {
        const next = current.map((item) => ({
          ...item,
          is_book_default: item.id === cardId,
        }));
        cardsRef.current = next;
        return next;
      });
      setNote(THIS_IS_NOW_YOUR_BOOK_STYLE);
    } catch (err) {
      const message = err?.message || '';
      setError(
        message.includes('Promote needs a resolved instruction')
          ? STYLE_NEEDS_SOMETHING
          : (message || 'Could not set the book style.'),
      );
    } finally {
      setBusy(false);
    }
  };

  const onPreset = async (presetId) => {
    if (!storyId || !presetId) return;
    setBusy(true);
    setError('');
    try {
      const card = await styleCardsApi.createFromPreset(storyId, presetId);
      if (!card) return;
      const next = [card, ...cardsRef.current.filter((item) => item.id !== card.id)];
      cardsRef.current = next;
      setCards(next);
      showStyle(card, { cardsNow: next });
    } catch (err) {
      setError(err?.message || 'Could not make a new style.');
    } finally {
      setBusy(false);
    }
  };

  const editOwnName = (value) => {
    setOwnName(value);
    ownDirtyRef.current = Boolean(value.trim() || ownSample.trim());
    syncUnloadGuard();
  };

  const editOwnSample = (value) => {
    const next = clampWords(value, 150);
    setOwnSample(next);
    ownDirtyRef.current = Boolean(ownName.trim() || next.trim());
    syncUnloadGuard();
  };

  const onSaveOwn = async () => {
    if (!storyId) return;
    const sample = clampWords(ownSample, 150).trim();
    if (!ownName.trim() || countWords(sample) < 1) {
      setError(PASTE_A_SAMPLE_FIRST);
      return;
    }
    setBusy(true);
    setError('');
    try {
      const card = await styleCardsApi.createFromWriting(storyId, {
        name: ownName.trim(),
        exemplars: sample,
      });
      if (!card) return;
      ownDirtyRef.current = false;
      setOwnName('');
      setOwnSample('');
      setOwnOpen(false);
      syncUnloadGuard();
      const next = [card, ...cardsRef.current.filter((item) => item.id !== card.id)];
      cardsRef.current = next;
      setCards(next);
      showStyle(card, { select: true, cardsNow: next });
    } catch (err) {
      const message = err?.message || '';
      setError(message.includes('Paste a sample') ? PASTE_A_SAMPLE_FIRST : (message || 'Could not save this style.'));
    } finally {
      setBusy(false);
    }
  };

  const setDial = (key, stop) => {
    const current = { ...(draft.dials || {}) };
    if (Number(current[key]) === stop) delete current[key];
    else current[key] = stop;
    editDraft({ dials: current });
  };

  const readyForBook = (card) => {
    if (card.id === openId) {
      return styleHasInstruction({
        text: draft.text,
        sample: draft.sample,
        dials: draft.dials,
      });
    }
    const fold = foldStyleNotes(card);
    return styleHasInstruction({
      text: fold.text,
      sample: card.exemplars,
      dials: card.dials,
    });
  };

  const setSceneCount = (cardId, count) => {
    setCards((current) => {
      const next = current.map((item) => (
        item.id === cardId ? { ...item, scene_count: count } : item
      ));
      cardsRef.current = next;
      return next;
    });
  };

  const loadUsage = async (card) => {
    if (!storyId || !card?.id) return;
    setError('');
    try {
      const next = await styleCardsApi.listScenes(storyId, card.id);
      setUsage({ cardId: card.id, ...(next || {}) });
      setUndoIds([]);
      if (next && typeof next.scene_count === 'number') setSceneCount(card.id, next.scene_count);
    } catch (err) {
      setError(err?.message || 'Could not list scenes.');
    }
  };

  const openUsage = (card) => {
    if (!storyId || !card?.id) return;
    if (card.id === openIdRef.current && !pickingRef.current) {
      void loadUsage(card);
      return;
    }
    runOrAsk(() => {
      showStyle(card, { keepUsage: true });
      void loadUsage(card);
    });
  };

  const onClearScene = async (sceneId) => {
    if (!storyId || !usage?.cardId) return;
    setBusy(true);
    try {
      await styleCardsApi.clearSceneStyle(storyId, sceneId);
      const nextCount = Math.max(0, (usage.scene_count || 1) - 1);
      setUsage((current) => ({
        ...current,
        scenes: (current?.scenes || []).filter((scene) => scene.id !== sceneId),
        scene_count: nextCount,
      }));
      setSceneCount(usage.cardId, nextCount);
    } catch (err) {
      setError(err?.message || 'Could not use the book style.');
    } finally {
      setBusy(false);
    }
  };

  const onClearAll = async () => {
    if (!storyId || !usage?.cardId) return;
    setBusy(true);
    try {
      const cleared = await styleCardsApi.clearScenes(storyId, usage.cardId);
      setUndoIds(cleared?.scene_ids || []);
      setUsage((current) => ({ ...current, scenes: [], scene_count: 0 }));
      setSceneCount(usage.cardId, 0);
      setNote(SCENES_BACK_ON_BOOK_STYLE);
    } catch (err) {
      setError(err?.message || 'Could not put scenes back on the book style.');
    } finally {
      setBusy(false);
    }
  };

  const onUndoClear = async () => {
    if (!storyId || !usage?.cardId || undoIds.length === 0) return;
    const cardId = usage.cardId;
    const restored = undoIds.slice();
    setNote('');
    setBusy(true);
    try {
      await styleCardsApi.restoreScenes(storyId, cardId, restored);
      setUndoIds([]);
      const next = await styleCardsApi.listScenes(storyId, cardId);
      const count = typeof next?.scene_count === 'number' ? next.scene_count : restored.length;
      setUsage({ cardId, ...(next || {}), scene_count: count });
      setSceneCount(cardId, count);
    } catch (err) {
      setError(err?.message || 'Could not undo.');
    } finally {
      setBusy(false);
    }
  };

  const askDelete = async (card) => {
    setMenuId(null);
    if (!storyId || !card?.id) return;
    try {
      const counts = await styleCardsApi.listScenes(storyId, card.id);
      setDeleteAsk({
        id: card.id,
        name: card.name || NEW_STYLE,
        sceneCount: counts?.scene_count || 0,
        chapterCount: counts?.chapter_count || 0,
        isBookStyle: Boolean(counts?.is_book_style || card.is_book_default),
      });
    } catch (err) {
      setError(err?.message || 'Could not check where this style is used.');
    }
  };

  const onDelete = async () => {
    const ask = deleteAsk;
    setDeleteAsk(null);
    if (!storyId || !ask?.id) return;
    setBusy(true);
    try {
      await styleCardsApi.deleteStyle(storyId, ask.id);
      const next = cards.filter((card) => card.id !== ask.id);
      cardsRef.current = next;
      setCards(next);
      if (openIdRef.current === ask.id) {
        draftDirtyRef.current = false;
        syncUnloadGuard();
        showStyle(next[0] || null, { cardsNow: next });
      }
    } catch (err) {
      setError(err?.message || 'Could not delete that style.');
    } finally {
      setBusy(false);
    }
  };

  const handlePick = useCallback((project, story) => {
    setProjectAndStory(project, story);
    setPickerOpen(false);
    navigate(styleCardsPath(story.id));
  }, [setProjectAndStory, navigate]);

  if (pickerOpen) {
    return (
      <StoryPicker
        isOpen
        title="Open a book for Your styles"
        scopedProject={currentProject}
        onClose={() => setPickerOpen(false)}
        onPick={handlePick}
      />
    );
  }

  if (!storyId) {
    return (
      <div className="sr-page style-room">
        <p className="sr-empty">{SELECT_BOOK_FOR_STYLE_NOTE}</p>
      </div>
    );
  }

  const sampleWords = countWords(draft.sample);
  const ownWords = countWords(ownSample);
  const openCard = cards.find((card) => card.id === openId) || null;
  const usageCard = cards.find((card) => card.id === usage?.cardId) || null;
  const stylesReady = stylesStatus === 'ready';
  const showStarter = stylesReady && ((cards.length === 0 && !formOpen) || picking);
  const showEditor = stylesReady && formOpen && !picking;
  const showList = stylesReady && cards.length > 0;

  const startPicker = (
    <section className="sr-start" aria-label={YOUR_STYLES}>
      <p className="sr-lede">{PICK_A_STARTING_SOUND}</p>
      <div className="sr-presets">
        {STYLE_PRESETS.map((preset) => (
          <button
            key={preset.id}
            type="button"
            className="sr-btn"
            disabled={busy}
            onClick={() => runOrAsk(() => { void onPreset(preset.id); })}
          >
            {preset.name}
          </button>
        ))}
      </div>
      <button type="button" className="sr-btn" disabled={busy} onClick={() => runOrAsk(() => { void createNew(); })}>
        {START_FROM_SCRATCH}
      </button>
      <button
        type="button"
        className="sr-btn"
        disabled={busy}
        aria-expanded={ownOpen}
        onClick={() => setOwnOpen((current) => !current)}
      >
        {START_FROM_MY_OWN_WRITING}
      </button>
      {ownOpen && (
        <div className="sr-own">
          <p className="sr-lede">{OWN_WRITING_LEDE}</p>
          <label className="sr-field" htmlFor="sr-own-name">
            <span>Name</span>
            <input
              id="sr-own-name"
              value={ownName}
              onChange={(event) => editOwnName(event.target.value)}
            />
          </label>
          <label className="sr-field" htmlFor="sr-own-sample">
            <span>{SAMPLE_OF_THE_SOUND}</span>
            <textarea
              id="sr-own-sample"
              value={ownSample}
              onChange={(event) => editOwnSample(event.target.value)}
            />
          </label>
          <p className="sr-meta">{SAMPLE_WORD_LIMIT(ownWords)}</p>
          <button
            type="button"
            className="sr-pill"
            disabled={busy || !ownName.trim() || ownWords < 1}
            onClick={onSaveOwn}
          >
            {STYLE_ROOM_SAVE}
          </button>
        </div>
      )}
    </section>
  );

  return (
    <div className="sr-page style-room">
      <header className="sr-header">
        <h1>{YOUR_STYLES}</h1>
      </header>
      {error && <p className="sr-error" role="alert">{error}</p>}
      <div className="sr-room">
        {stylesStatus === 'loading' && (
          <div className="sr-loading" role="status" aria-label={LOADING_STYLE_NOTES}>
            <Loading size="small" />
          </div>
        )}
        {showList && (
            <section className="sr-list" aria-label={YOUR_STYLES}>
              <button type="button" className="sr-pill sr-pill--outline" disabled={busy} onClick={() => runOrAsk(showPicker)}>
                {NEW_STYLE}
              </button>
              <ul className="sr-styles">
                  {cards.map((card) => {
                    const ready = readyForBook(card);
                    const selected = card.id === openId;
                    return (
                      <li
                        key={card.id}
                        className={selected ? 'sr-style sr-style--selected' : 'sr-style'}
                        onClick={(event) => {
                          const control = event.target.closest('button, a');
                          if (control && !control.classList.contains('sr-style__name')) return;
                          openStyle(card, { cardsNow: cards });
                        }}
                      >
                        <div className="sr-style__head">
                          <button
                            type="button"
                            className="sr-style__name"
                            onClick={(event) => {
                              event.stopPropagation();
                              openStyle(card, { cardsNow: cards });
                            }}
                          >
                            {card.name}
                          </button>
                          {card.is_book_default && <span className="sr-tag">{BOOK_STYLE}</span>}
                        </div>
                        <button
                          type="button"
                          className="sr-style__used"
                          onClick={(event) => {
                            event.stopPropagation();
                            openUsage(card);
                          }}
                        >
                          {usedInLabel(card.scene_count || 0, card.is_book_default)}
                        </button>
                        {!ready && <p className="sr-note">{STYLE_NEEDS_SOMETHING}</p>}
                        <div className="sr-style__actions">
                          {ready && (
                            <button
                              type="button"
                              className="sr-style__make"
                              disabled={busy}
                              onClick={() => onMakeBook(card.id)}
                            >
                              {USE_FOR_THIS_BOOK}
                            </button>
                          )}
                          <button
                            type="button"
                            className="sr-style__more"
                            aria-expanded={menuId === card.id}
                            aria-label={STYLE_ACTIONS_FOR(card.name)}
                            onClick={() => setMenuId((current) => (current === card.id ? null : card.id))}
                          >
                            <span aria-hidden="true">{'\u22EF'}</span>
                          </button>
                        </div>
                        {menuId === card.id && (
                          <div className="sr-menu">
                            <button type="button" className="sr-btn" onClick={() => runOrAsk(() => { void onCopy(card); })}>{MAKE_A_COPY}</button>
                            <button
                              type="button"
                              className="sr-btn"
                              onClick={() => {
                                setMenuId(null);
                                openStyle(card, { select: true, cardsNow: cards });
                              }}
                            >
                              {STYLE_RENAME}
                            </button>
                            <button type="button" className="sr-btn sr-btn--danger" onClick={() => askDelete(card)}>
                              {DELETE_STYLE}
                            </button>
                          </div>
                        )}
                      </li>
                    );
                  })}
                </ul>
            </section>
        )}
        {showStarter && startPicker}
        {showEditor && (
              <section className="sr-editor" aria-label={openCard?.name || NEW_STYLE}>
                <div className="sr-editor__body">
                <label className="sr-field" htmlFor="sr-name">
                  <span>Name</span>
                  <input
                    id="sr-name"
                    ref={nameRef}
                    value={draft.name}
                    onChange={(event) => editDraft({ name: event.target.value })}
                  />
                </label>
                {draft.copiedFrom && <p className="sr-note">{COPIED_FROM(draft.copiedFrom)}</p>}
                {draft.moved && <p className="sr-banner" role="status">{MOVED_NOTES_INTO_STYLE}</p>}
                <div className="sr-dials">
                  {DIALS.map((dial) => (
                    <DialScale
                      key={dial.key}
                      dial={dial}
                      value={draft.dials?.[dial.key]}
                      onPick={setDial}
                    />
                  ))}
                </div>
                <div className="sr-field">
                  <div className="sr-field__label">
                    <label htmlFor="sr-text">{HOW_WRITING_SHOULD_SOUND}</label>
                    <span className="sr-optional">{STYLE_FIELD_OPTIONAL}</span>
                  </div>
                  <textarea
                    id="sr-text"
                    value={draft.text}
                    onChange={(event) => editDraft({ text: event.target.value, moved: false })}
                  />
                </div>
                <div className="sr-field">
                  <div className="sr-field__label">
                    <label htmlFor="sr-sample">{SAMPLE_OF_THE_SOUND}</label>
                    <span className="sr-optional">{STYLE_FIELD_OPTIONAL}</span>
                  </div>
                  <textarea
                    id="sr-sample"
                    value={draft.sample}
                    onChange={(event) => editDraft({ sample: event.target.value })}
                  />
                </div>
                {usage && (
                  <div className="sr-usage">
                    <h2>{usedInLabel(usage.scene_count || 0, usageCard?.is_book_default)}</h2>
                    <ul>
                      {(usage.scenes || []).map((scene) => (
                        <li key={scene.id}>
                          <span>{scene.sequence ? `${scene.sequence} · ` : ''}{scene.name}</span>
                          <a
                            href={draftThisScenePath(storyId, { sceneId: scene.id, title: scene.name })}
                            onClick={(event) => {
                              if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || event.button !== 0) return;
                              event.preventDefault();
                              const path = draftThisScenePath(storyId, { sceneId: scene.id, title: scene.name });
                              runOrAsk(() => navigate(path));
                            }}
                          >
                            {OPEN_SCENE}
                          </a>
                          <button type="button" className="sr-btn" disabled={busy} onClick={() => onClearScene(scene.id)}>
                            {USE_THE_BOOK_STYLE}
                          </button>
                        </li>
                      ))}
                    </ul>
                    <button type="button" className="sr-btn" disabled={busy} onClick={onClearAll}>
                      {PUT_ALL_BACK_ON_BOOK_STYLE}
                    </button>
                    {undoIds.length > 0 && (
                      <button type="button" className="sr-btn" disabled={busy} onClick={onUndoClear}>
                        {UNDO_ACTION}
                      </button>
                    )}
                  </div>
                )}
                </div>
                <div className="sr-editor__foot">
                  <div className="sr-editor__bar">
                    <p className="sr-meta">{SAMPLE_WORD_LIMIT(sampleWords)}</p>
                    <button type="button" className="sr-pill" disabled={busy || !openId} onClick={onSave}>
                      {STYLE_ROOM_SAVE}
                    </button>
                  </div>
                  {note && <p className="sr-note" role="status">{note}</p>}
                </div>
              </section>
        )}
      </div>
      <ConfirmModal
        isOpen={leaveOpen}
        title={UNSAVED_STYLE_CONFIRM}
        confirmLabel={DISCARD_UNSAVED}
        cancelLabel={KEEP_EDITING}
        destructive
        onConfirm={confirmLeave}
        onCancel={cancelLeave}
      />
      <ConfirmModal
        isOpen={Boolean(deleteAsk)}
        title={DELETE_STYLE}
        message={deleteAsk ? deleteStyleWarning(deleteAsk) : ''}
        confirmLabel={DELETE_STYLE}
        cancelLabel={KEEP_EDITING}
        destructive
        onConfirm={onDelete}
        onCancel={() => setDeleteAsk(null)}
      />
    </div>
  );
}
